import { useMemo } from "react";

import { useCardsQuery } from "@/api/cards";
import { usePendingUserCountQuery } from "@/api/tenants";
import { useAuth } from "@/auth";
import type { TrKey } from "@/i18n";

export interface NavItem {
  id: string;
  path: string;
  labelKey: TrKey;
  visible: boolean;
  /** Gorev B (esitlik) — yalniz "kullanicilar" sekmesinde, N>0 iken turuncu rozet. */
  badge?: number;
}

/**
 * Backend `TABS` (app/core/capabilities.py) yalniz platform capability'sine
 * gore ozet/kpi/stok/yorumlar/denetim gorunurlugunu belirler — bu bilgi
 * `/api/v2/cards` yanitindaki `tabs[]`'ten okunur. Backend'in kendi "admin"
 * girdisi ESKI/legacy sekmedir (capability kapisi yok); yeni IA'daki
 * /ayarlar, /kurulum, /kullanicilar, /admin ROL'e gore kapanir (sozlesme §2
 * Yetki sutunu: tenant_admin / vdd_super) — bu yuzden asagida AYRI ele alinir.
 */
const CAPABILITY_GATED_TABS: Array<{ id: string; path: string; labelKey: TrKey }> = [
  { id: "ozet", path: "/ozet", labelKey: "nav.ozet" },
  { id: "kpi", path: "/kpi", labelKey: "nav.kpi" },
  { id: "stok", path: "/stok", labelKey: "nav.stok" },
  { id: "yorumlar", path: "/yorumlar", labelKey: "nav.yorumlar" },
  { id: "denetim", path: "/denetim", labelKey: "nav.denetim" },
];

export function useVisibleTabs(): NavItem[] {
  const { principal } = useAuth();
  const cardsQuery = useCardsQuery();

  const tabVisibility = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const tab of cardsQuery.data?.tabs ?? []) map.set(tab.id, tab.visible);
    return map;
  }, [cardsQuery.data]);

  const isTenantAdmin = principal?.role === "tenant_admin" || Boolean(principal?.user.is_super);
  const isSuper = Boolean(principal?.user.is_super);
  // Gorev B (esitlik) — yalniz tenant_admin/super icin, "kullanicilar"
  // sekmesinin yanindaki bekleyen-onay rozeti (bkz. api/tenants.ts).
  const pendingCountQuery = usePendingUserCountQuery(isTenantAdmin);

  return useMemo(() => {
    const items: NavItem[] = CAPABILITY_GATED_TABS.map((tab) => ({
      ...tab,
      // Cards yaniti gelmeden once (ilk yukleme) gorunur varsayilir — ani
      // "kayboldu" flash'i onlemek icin; yanit gelince gercek deger uygulanir.
      visible: tabVisibility.has(tab.id) ? (tabVisibility.get(tab.id) ?? true) : true,
    }));

    items.push({ id: "ayarlar", path: "/ayarlar", labelKey: "nav.ayarlar", visible: isTenantAdmin });
    items.push({ id: "kurulum", path: "/kurulum", labelKey: "nav.kurulum", visible: isTenantAdmin });
    items.push({
      id: "kullanicilar",
      path: "/kullanicilar",
      labelKey: "nav.kullanicilar",
      visible: isTenantAdmin,
      badge: pendingCountQuery.data?.count,
    });
    items.push({ id: "admin", path: "/admin", labelKey: "nav.admin", visible: isSuper });

    return items;
  }, [tabVisibility, isTenantAdmin, isSuper, pendingCountQuery.data]);
}
